import { Box, Skeleton, Typography } from '@mui/material';
import { ApexOptions } from 'apexcharts';
import { useMemo } from 'react';
import Chart from 'react-apexcharts';

import { colors } from 'src/global/styles/colors';
import { useAnalyticsContext } from 'src/hooks/useAnalyticsContext';
import { useGetProjectStatisticsQuery } from 'src/store/services/analytics.service';

export function TopProjectsChart() {
  const { address, network } = useAnalyticsContext();

  const {
    data: projectStats,
    isLoading,
    error,
  } = useGetProjectStatisticsQuery(
    {
      page: 1,
      size: 100,
      address,
      network,
    },
    { skip: !address || !network },
  );

  const chartData = useMemo(() => {
    if (!projectStats?.data.length) {
      return [{ name: 'Revenue', data: [] }];
    }

    const sortedProjects = [...projectStats.data].sort((a, b) => b.revenues - a.revenues).slice(0, 6);

    return [
      {
        name: 'Revenue',
        data: sortedProjects.map(project => ({
          x: project.name || 'Unknown Project',
          y: project.revenues / 1000,
        })),
      },
    ];
  }, [projectStats]);

  const chartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      background: 'transparent',
      toolbar: {
        show: false,
      },
      fontFamily: 'Gotham',
    },
    theme: {
      mode: 'dark',
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 2,
        barHeight: '70%',
      },
    },
    dataLabels: {
      enabled: false,
    },
    colors: ['#495DB6'],
    grid: {
      show: true,
      borderColor: 'rgba(255, 255, 255, 0.03)',
      strokeDashArray: 6,
      yaxis: {
        lines: {
          show: true,
        },
      },
      xaxis: {
        lines: {
          show: false,
        },
      },
    },
    xaxis: {
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: colors.neutral90,
          fontSize: '12px',
          fontFamily: 'Gotham',
          fontWeight: 400,
        },
        formatter: (value: number) => {
          if (value >= 1000) {
            return `$${(value / 1000).toFixed(1)}M`;
          } else if (value >= 1) {
            return `$${value.toFixed(0)}K`;
          }
          return `$${value.toFixed(1)}K`;
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: colors.neutral90,
          fontSize: '12px',
          fontFamily: 'Gotham',
          fontWeight: 400,
        },
      },
    },
    tooltip: {
      theme: 'dark',
      style: {
        fontSize: '12px',
        fontFamily: 'Gotham',
      },
      x: {
        show: false,
      },
      y: {
        formatter: (value: number) => {
          if (value >= 1000) {
            return `$${(value / 1000).toFixed(1)}M`;
          }
          return `$${value.toFixed(1)}K`;
        },
      },
    },
  };

  return (
    <Box
      sx={{
        padding: 3,
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        backdropFilter: 'blur(100px)',
        borderRadius: '8px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Typography
        sx={{
          fontSize: '20px',
          fontWeight: 700,
          color: colors.white,
          fontFamily: 'Gotham',
          marginBottom: 3,
        }}>
        Top Projects By Sales
      </Typography>

      <Box sx={{ flex: 1, minHeight: 320 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Skeleton variant='rectangular' width='100%' height={250} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography sx={{ color: colors.red200, textAlign: 'center' }}>Failed to load top projects data</Typography>
          </Box>
        ) : chartData[0].data.length > 0 ? (
          <Chart options={chartOptions} series={chartData} type='bar' height='100%' />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography sx={{ color: colors.neutral90, textAlign: 'center' }}>No project data available</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
